import { recordHook } from '@flatfile/plugin-record-hook'
import {
  formatCurrency,
  findUSState,
  convertToLowercase,
  convertToUppercase,
  formatAndValidateCurrency,
  formatAndValidatePercentage,
} from 'utils'
import * as yup from 'yup'
import {
  BD_TRANSACTION_TYPES,
  BD_PAYMENT_INSTRUCTIONS,
  PAYMENT_METHODS,
} from 'config/benefit-distributions'
import { invalidPostalCodes } from 'config/products'

const numericRegex = /[0-9]/g
const removeCurrencySymbolAndParse = (value) => {
  // Regular expression to remove any non-digit characters except '.', '-'
  const sanitizedValue = value.replace(/[^\d.]/g, '')
  return parseFloat(sanitizedValue) || 0
}

/** FORMATTINGS **/
/* The folowings are product specific formattings and validations for Benefit Distributions 
  any formatting or validation specific to benefit distributions should be added here*/

export const zipForCheckFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const zipForCheck = record.get('zipForCheck')
      if (zipForCheck) {
        const sanitizedInput = zipForCheck.replaceAll(/[^a-zA-Z0-9- ]/g, '')
        let sanitizedZipCode = sanitizedInput.match(numericRegex)?.join('')
        if (sanitizedZipCode) {
          record.set('zipForCheck', sanitizedZipCode)
          if (sanitizedZipCode.length === 4 || sanitizedZipCode.length === 8) {
            sanitizedZipCode = '0' + sanitizedZipCode
            record.set('zipForCheck', sanitizedZipCode)
          } else if (
            sanitizedZipCode.length === 3 ||
            sanitizedZipCode.length === 7
          ) {
            sanitizedZipCode = '00' + sanitizedZipCode
            record.set('zipForCheck', sanitizedZipCode)
          } else if (
            sanitizedZipCode.length === 2 ||
            sanitizedZipCode.length === 6
          ) {
            sanitizedZipCode = '000' + sanitizedZipCode
            record.set('zipForCheck', sanitizedZipCode)
          }
        }
        if (sanitizedZipCode.length > 5) {
          record.set(
            'zipForCheck',
            sanitizedZipCode.slice(0, 5) + '-' + sanitizedZipCode.slice(5)
          )
        }
      }
      return record
    })
  )
}

export const amountFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const amount = record.get('amount')
      if (amount) {
        formatAndValidateCurrency(amount, 'amount', record)
      }
      return record
    })
  )
}

export const tpaBenefitDistributionProcessingFeeFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const tpaBenefitDistributionProcessingFee = record.get(
        'tpaBenefitDistributionProcessingFee'
      )
      if (tpaBenefitDistributionProcessingFee) {
        formatAndValidateCurrency(
          tpaBenefitDistributionProcessingFee,
          'tpaBenefitDistributionProcessingFee',
          record
        )
      }
      return record
    })
  )
}

export const inspiraBenefitDisbursementProcessingFeeFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const transactionType = record.get('transactionType')
      if (
        transactionType.toLowerCase() ===
        BD_TRANSACTION_TYPES.check.toLowerCase()
      ) {
        record.set(
          'inspiraBenefitDisbursementProcessingFee',
          formatCurrency('35')
        )
      } else if (transactionType.toUpperCase() === BD_TRANSACTION_TYPES.ach) {
        record.set(
          'inspiraBenefitDisbursementProcessingFee',
          formatCurrency('35')
        )
        record.set('transactionType', convertToUppercase(transactionType))
      } else if (
        [
          BD_TRANSACTION_TYPES.wire.toLocaleLowerCase(),
          BD_TRANSACTION_TYPES.overnight.toLocaleLowerCase(),
        ].includes(transactionType.toLowerCase())
      ) {
        record.set(
          'inspiraBenefitDisbursementProcessingFee',
          formatCurrency('65')
        )
      } else {
        record.set('inspiraBenefitDisbursementProcessingFee', null)
      }
      return record
    })
  )
}

export const transactionTypeFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const transactionType = record.get('transactionType')
      if (transactionType.toUpperCase() !== BD_TRANSACTION_TYPES.ach) {
        record.set('transactionType', convertToLowercase(transactionType))
      }
      return record
    })
  )
}

export const paymentInstructionsFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const paymentInstructions = record.get('paymentInstructions')
      if (paymentInstructions) {
        record.set(
          'paymentInstructions',
          convertToLowercase(paymentInstructions)
        )
      }
      return record
    })
  )
}

export const grossDistributionAndAmountFormattingListener = (
  listener,
  sheetSlug,
  paymentMethod
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const amount = record.get('amount')
      const tpaBenefitDistributionProcessingFee = record.get(
        'tpaBenefitDistributionProcessingFee'
      )
      const inspiraBenefitDisbursementProcessingFee = record.get(
        'inspiraBenefitDisbursementProcessingFee'
      )

      if (amount && inspiraBenefitDisbursementProcessingFee) {
        let amountInput = removeCurrencySymbolAndParse(amount)

        let inspiraProcessingFeeInput = removeCurrencySymbolAndParse(
          inspiraBenefitDisbursementProcessingFee
        )

        let tpaProcessingFeeInput = tpaBenefitDistributionProcessingFee
          ? removeCurrencySymbolAndParse(tpaBenefitDistributionProcessingFee)
          : 0

        let distributionAmountInputDD = (
          amountInput -
          (tpaProcessingFeeInput + inspiraProcessingFeeInput)
        ).toFixed(2)
        let distributionAmountInputCC = (
          amountInput - tpaProcessingFeeInput
        ).toFixed(2)

        if (paymentMethod == PAYMENT_METHODS.deductFromDistribution) {
          if (distributionAmountInputDD) {
            formatAndValidateCurrency(
              distributionAmountInputDD.toString(),
              'grossDistributionAmount',
              record
            )
            formatAndValidateCurrency(
              distributionAmountInputDD.toString(),
              'grossDistribution',
              record
            )
          }
        }
        if (paymentMethod == PAYMENT_METHODS.creditCard) {
          if (distributionAmountInputCC) {
            formatAndValidateCurrency(
              distributionAmountInputCC.toString(),
              'grossDistributionAmount',
              record
            )
            formatAndValidateCurrency(
              distributionAmountInputCC.toString(),
              'grossDistribution',
              record
            )
          }
        }
      }
      return record
    })
  )
}

export const capitalGainFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const capitalGain = record.get('capitalGain')
      if (capitalGain) {
        formatAndValidateCurrency(capitalGain, 'capitalGain', record)
      }
      return record
    })
  )
}

export const netAppreciationFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const netAppreciation = record.get('netAppreciation')
      if (netAppreciation) {
        formatAndValidateCurrency(netAppreciation, 'netAppreciation', record)
      }
      return record
    })
  )
}

export const totalEmployeeContributionsFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const totalEmployeeContributions = record.get(
        'totalEmployeeContributions'
      )
      if (totalEmployeeContributions) {
        formatAndValidateCurrency(
          totalEmployeeContributions,
          'totalEmployeeContributions',
          record
        )
      }
      return record
    })
  )
}

export const amountAllocableFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const amountAllocable = record.get('amountAllocable')
      if (amountAllocable) {
        formatAndValidateCurrency(amountAllocable, 'amountAllocable', record)
      }
      return record
    })
  )
}

export const stateForCheckFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const stateForCheck = record.get('stateForCheck')
      if (stateForCheck) {
        const matchingState = findUSState(stateForCheck)
        if (matchingState) {
          record.set('stateForCheck', matchingState.value)
        } else {
          record.addError('stateForCheck', 'Must be a valid US state')
        }
      }
      return record
    })
  )
}

export const taxableAmountFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const taxableAmount = record.get('taxableAmount')
      if (taxableAmount) {
        formatAndValidateCurrency(taxableAmount, 'taxableAmount', record)
      }
      return record
    })
  )
}

export const taxableAmountNotDeterminedFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const taxableAmountNotDetermined = record.get(
        'taxableAmountNotDetermined'
      )
      if (taxableAmountNotDetermined) {
        record.set(
          'taxableAmountNotDetermined',
          convertToUppercase(taxableAmountNotDetermined)
        )
      }
      return record
    })
  )
}

export const totalDistributionFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const totalDistribution = record.get('totalDistribution')
      if (totalDistribution) {
        record.set('totalDistribution', convertToUppercase(totalDistribution))
      }
      return record
    })
  )
}

export const employeeContributionsFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const employeeContributions = record.get('employeeContributions')
      if (employeeContributions) {
        formatAndValidateCurrency(
          employeeContributions,
          'employeeContributions',
          record
        )
      }
      return record
    })
  )
}

export const federalIncomeTaxFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const federalIncomeTax = record.get('federalIncomeTax')
      if (federalIncomeTax) {
        formatAndValidatePercentage(
          federalIncomeTax,
          'federalIncomeTax',
          record
        )
      }
      return record
    })
  )
}

export const federalIncomeTaxAmountFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const federalIncomeTaxAmount = record.get('federalIncomeTaxAmount')
      if (federalIncomeTaxAmount) {
        formatAndValidateCurrency(
          federalIncomeTaxAmount,
          'federalIncomeTaxAmount',
          record
        )
      }
      return record
    })
  )
}

export const percentageTotalDistributionFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const percentageTotalDistribution = record.get(
        'percentageTotalDistribution'
      )
      if (percentageTotalDistribution) {
        formatAndValidatePercentage(
          percentageTotalDistribution,
          'percentageTotalDistribution',
          record
        )
      }
      return record
    })
  )
}

export const distributionCodeFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const distributionCode = record.get('distributionCode')
      if (distributionCode) {
        record.set('distributionCode', convertToUppercase(distributionCode))
      }
      return record
    })
  )
}

export const stateTaxWithheldFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const stateTaxWithheld = record.get('stateTaxWithheld')
      if (stateTaxWithheld) {
        formatAndValidatePercentage(
          stateTaxWithheld,
          'stateTaxWithheld',
          record
        )
      }
      return record
    })
  )
}

export const stateTaxWithheldAmountFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const stateTaxWithheldAmount = record.get('stateTaxWithheldAmount')
      if (stateTaxWithheldAmount) {
        formatAndValidateCurrency(
          stateTaxWithheldAmount,
          'stateTaxWithheldAmount',
          record
        )
      }
      return record
    })
  )
}

/** CONSTRAINTS **/

export const emailAddressValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const emailAddress = record.get('emailAddress')
      if (emailAddress) {
        const schema = yup.string().email()
        const isValidEmail = schema.isValidSync(emailAddress)
        if (!isValidEmail) {
          record.addError(
            'emailAddress',
            'Must be valid email format: example@email.com'
          )
        }
      }
      return record
    })
  )
}

export const paymentInstructionsValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const transactionType = record.get('transactionType')
      const paymentInstructions = record.get('paymentInstructions')
      if (
        transactionType === BD_TRANSACTION_TYPES.ach &&
        paymentInstructions == BD_PAYMENT_INSTRUCTIONS.directRollover
      ) {
        record.addError(
          'paymentInstructions',
          'Invalid if ACH & payment instructions is direct rollover'
        )
      }
      return record
    })
  )
}

export const wireAchwireAch2wireAch3AbaRoutingValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const transactionType = record.get('transactionType')
      const wireAch = record.get('wireAch')
      const wireAch2 = record.get('wireAch2')
      const wireAch3 = record.get('wireAch3')
      const abaRouting = record.get('abaRouting')
      if (
        transactionType === BD_TRANSACTION_TYPES.wire ||
        transactionType === BD_TRANSACTION_TYPES.ach
      ) {
        if (!wireAch) {
          record.addError('wireAch', 'Required if wire or ACH, blank if check')
        }
        if (!abaRouting) {
          record.addError(
            'abaRouting',
            'Required if wire or ACH, blank if check'
          )
        }
        if (!wireAch2) {
          record.addError('wireAch2', 'Required if wire or ACH, blank if check')
        }
        if (!wireAch3) {
          record.addError('wireAch3', 'Required if wire or ACH, blank if check')
        }
      }
      return record
    })
  )
}
export const ffcNameAndffcNumberValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const transactionType = record.get('transactionType')
      const paymentInstructions = record.get('paymentInstructions')
      const ffcName = record.get('ffcName')
      const ffcNumber = record.get('ffcNumber')
      if (
        transactionType === BD_TRANSACTION_TYPES.wire &&
        paymentInstructions === BD_PAYMENT_INSTRUCTIONS.directRollover
      ) {
        if (!ffcName) {
          record.addError(
            'ffcName',
            'Required for wire and direct rollover, blank if check'
          )
        }
        if (!ffcNumber) {
          record.addError(
            'ffcNumber',
            'Required for wire and direct rollover, blank if check'
          )
        }
      }
      return record
    })
  )
}

export const accountTypevalidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const accountType = record.get('accountType')
      const transactionType = record.get('transactionType')
      if (
        (transactionType === BD_TRANSACTION_TYPES.wire ||
          transactionType === BD_TRANSACTION_TYPES.ach) &&
        !accountType
      ) {
        record.addError(
          'accountType',
          'Required if wire or ACH, blank if check'
        )
      }
      return record
    })
  )
}

export const checkPayableInfoValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const transactionType = record.get('transactionType')
      const checkPayableInfo = record.get('checkPayableInfo')
      if (
        (transactionType === BD_TRANSACTION_TYPES.check ||
          transactionType === BD_TRANSACTION_TYPES.overnight) &&
        !checkPayableInfo
      ) {
        record.addError(
          'checkPayableInfo',
          'Required if check, blank if wire or ACH'
        )
      }
      return record
    })
  )
}

export const accountNumberValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const transactionType = record.get('transactionType')
      const paymentInstructions = record.get('paymentInstructions')
      const accountNumber = record.get('accountNumber')
      if (
        (transactionType === BD_TRANSACTION_TYPES.check &&
          paymentInstructions === BD_PAYMENT_INSTRUCTIONS.directRollover) ||
        (transactionType === BD_TRANSACTION_TYPES.overnight &&
          paymentInstructions === BD_PAYMENT_INSTRUCTIONS.directRollover)
      ) {
        if (!accountNumber) {
          record.addError(
            'accountNumber',
            'Required for check and direct rollover, blank for Wire or ACH'
          )
        }
      }
      return record
    })
  )
}

export const addressForCheckValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const transactionType = record.get('transactionType')
      const paymentInstructions = record.get('paymentInstructions')
      const addressForCheck = record.get('addressForCheck')
      const cityForCheck = record.get('cityForCheck')
      const stateForCheck = record.get('stateForCheck')
      const zipForCheck = record.get('zipForCheck')
      if (
        (transactionType === BD_TRANSACTION_TYPES.check &&
          paymentInstructions === BD_PAYMENT_INSTRUCTIONS.directRollover) ||
        (transactionType === BD_TRANSACTION_TYPES.overnight &&
          paymentInstructions === BD_PAYMENT_INSTRUCTIONS.directRollover)
      ) {
        if (!addressForCheck) {
          record.addError(
            'addressForCheck',
            'Required when check and direct rollover, blank if wire or ACH'
          )
        }
        if (!cityForCheck) {
          record.addError(
            'cityForCheck',
            'Required when check and direct rollover, blank if wire or ACH'
          )
        }
        if (!stateForCheck) {
          record.addError(
            'stateForCheck',
            'Required when check and direct rollover, blank if wire or ACH'
          )
        }
        if (!zipForCheck) {
          record.addError(
            'zipForCheck',
            'Required when check and direct rollover, blank if wire or ACH'
          )
        }
      }
      return record
    })
  )
}

export const zipForCheckValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const zipForCheck = record.get('zipForCheck')
      if (zipForCheck) {
        const zipCodeRe = new RegExp('^[0-9]{5}(?:-[0-9]{4})?$')
        const postalCode = zipForCheck.substring(0, 5)
        if (!zipCodeRe.test(zipForCheck)) {
          record.addError(
            'zipForCheck',
            'US Zip Codes must have format: XXXXX or XXXXX-XXXX'
          )
        } else if (invalidPostalCodes.includes(postalCode)) {
          record.addError('zipForCheck', 'Please enter a valid Zip Code')
        }
      }
      return record
    })
  )
}

export const payerStateNumberValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const payerStateNumber = record.get('payerStateNumber')
      if (payerStateNumber) {
        const matchingState = findUSState(payerStateNumber)
        if (matchingState) {
          record.set('payerStateNumber', matchingState.value)
        } else {
          record.addError('payerStateNumber', 'Must be a valid US state')
        }
      }
      return record
    })
  )
}

export const federalIncomeTaxAndFederalIncomeTaxAmountValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const federalIncomeTax = record.get('federalIncomeTax')
      const federalIncomeTaxAmount = record.get('federalIncomeTaxAmount')
      if (federalIncomeTax && federalIncomeTaxAmount) {
        record.addError(
          'federalIncomeTax',
          'Either the Federal income tax withheld (Box 4) - percent (%) or the Federal income tax withheld (Box 4) - ($) must have a value, not both'
        )
        record.addError(
          'federalIncomeTaxAmount',
          'Either the Federal income tax withheld (Box 4) - percent (%) or the Federal income tax withheld (Box 4) - ($) must have a value, not both'
        )
      }
      return record
    })
  )
}

export const stateTaxWithheldAndStateTaxWithheldAmountValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const stateTaxWithheld = record.get('stateTaxWithheld')
      const stateTaxWithheldAmount = record.get('stateTaxWithheldAmount')
      if (stateTaxWithheld && stateTaxWithheldAmount) {
        record.addError(
          'stateTaxWithheld',
          'Either the State tax withheld (Box 14) - percent (%) or the State tax withheld (Box 14) - ($) must have a value, not both'
        )
        record.addError(
          'stateTaxWithheldAmount',
          'Either the State tax withheld (Box 14) - percent (%) or the State tax withheld (Box 14) - ($) must have a value, not both'
        )
      }
      return record
    })
  )
}
